import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeftRight, Plus, FileText, BarChart3, TrendingUp, Package, IndianRupee, Calendar, Building2, Download } from "lucide-react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

type TransferType = "Stock" | "Funds" | "Assets" | "Expense Recharge";
type TransferStatus = "Pending" | "Approved" | "Completed" | "Rejected";

interface BranchTransfer {
  id: string;
  date: string;
  fromBranchId: string;
  toBranchId: string;
  type: TransferType;
  description: string;
  items: TransferItem[];
  totalValue: number;
  status: TransferStatus;
  journalEntries: JournalEntry[];
  reference: string;
  narration: string;
  createdBy: string;
  createdAt: string;
  approvedBy?: string;
  approvedAt?: string;
}

interface TransferItem {
  name: string;
  quantity: number;
  rate: number;
  value: number;
  hsnCode?: string;
}

interface JournalEntry {
  ledger: string;
  branch: string;
  debit: number;
  credit: number;
}

const SEED_TRANSFERS: BranchTransfer[] = [];

const CHART_COLORS = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))"];

const STATUS_VARIANT: Record<TransferStatus, "default" | "secondary" | "destructive" | "outline"> = {
  Completed: "default",
  Approved: "secondary",
  Pending: "outline",
  Rejected: "destructive",
};

export default function InterBranchAccountingPage() {
  const { branches } = useBranch();
  const { user } = useAuth();
  const { toast } = useToast();
  const [transfers, setTransfers] = useState<BranchTransfer[]>(SEED_TRANSFERS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<BranchTransfer | null>(null);
  const [filterType, setFilterType] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  // New transfer form state
  const [newTransfer, setNewTransfer] = useState({
    fromBranchId: "", toBranchId: "", type: "Stock" as TransferType,
    narration: "", items: [{ name: "", quantity: 1, rate: 0, value: 0, hsnCode: "" }] as TransferItem[],
  });

  const getBranchName = (id: string) => branches.find((b) => b.id === id)?.name || id;

  const filteredTransfers = useMemo(() => {
    return transfers.filter((t) => {
      if (filterType !== "all" && t.type !== filterType) return false;
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      return true;
    });
  }, [transfers, filterType, filterStatus]);

  // Analytics
  const branchTransferVolume = useMemo(() => {
    const map: Record<string, { name: string; sent: number; received: number }> = {};
    branches.forEach((b) => { map[b.id] = { name: b.name.split(" ")[0], sent: 0, received: 0 }; });
    transfers.filter((t) => t.status === "Completed").forEach((t) => {
      if (map[t.fromBranchId]) map[t.fromBranchId].sent += t.totalValue;
      if (map[t.toBranchId]) map[t.toBranchId].received += t.totalValue;
    });
    return Object.values(map);
  }, [branches, transfers]);

  const typeDistribution = useMemo(() => {
    const map: Record<string, number> = {};
    transfers.forEach((t) => { map[t.type] = (map[t.type] || 0) + t.totalValue; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [transfers]);

  const monthlyTrend = useMemo(() => {
    const map: Record<string, number> = {};
    transfers.filter((t) => t.status === "Completed").forEach((t) => {
      const month = t.date.slice(0, 7);
      map[month] = (map[month] || 0) + t.totalValue;
    });
    return Object.entries(map).sort().map(([month, value]) => ({ month, value }));
  }, [transfers]);

  const totalTransferred = transfers.filter((t) => t.status === "Completed").reduce((s, t) => s + t.totalValue, 0);
  const pendingCount = transfers.filter((t) => t.status === "Pending" || t.status === "Approved").length;

  const addItem = () => {
    setNewTransfer((prev) => ({
      ...prev,
      items: [...prev.items, { name: "", quantity: 1, rate: 0, value: 0, hsnCode: "" }],
    }));
  };

  const updateItem = (idx: number, field: keyof TransferItem, val: string | number) => {
    setNewTransfer((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: val };
      items[idx].value = items[idx].quantity * items[idx].rate;
      return { ...prev, items };
    });
  };

  const removeItem = (idx: number) => {
    if (newTransfer.items.length <= 1) return;
    setNewTransfer((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const handleCreateTransfer = () => {
    if (!newTransfer.fromBranchId || !newTransfer.toBranchId || newTransfer.fromBranchId === newTransfer.toBranchId) {
      toast({ title: "Invalid branches", description: "Source and destination branches must be different.", variant: "destructive" });
      return;
    }
    const validItems = newTransfer.items.filter((i) => i.name && i.quantity > 0 && i.rate > 0);
    if (validItems.length === 0) {
      toast({ title: "No items", description: "Add at least one item with valid quantity and rate.", variant: "destructive" });
      return;
    }

    const totalValue = validItems.reduce((s, i) => s + i.value, 0);
    const fromName = getBranchName(newTransfer.fromBranchId);
    const toName = getBranchName(newTransfer.toBranchId);

    const ledgerName = newTransfer.type === "Stock" ? "Branch Stock (Inventory)"
      : newTransfer.type === "Funds" ? "Branch Current A/c"
      : newTransfer.type === "Assets" ? "Fixed Assets"
      : "Branch Expenses";

    const transfer: BranchTransfer = {
      id: `ibt-${crypto.randomUUID().slice(0, 8)}`,
      date: new Date().toISOString().split("T")[0],
      fromBranchId: newTransfer.fromBranchId,
      toBranchId: newTransfer.toBranchId,
      type: newTransfer.type,
      description: validItems.map((i) => i.name).join(", "),
      items: validItems,
      totalValue,
      status: "Pending",
      journalEntries: [
        { ledger: ledgerName, branch: toName, debit: totalValue, credit: 0 },
        { ledger: ledgerName, branch: fromName, debit: 0, credit: totalValue },
      ],
      reference: `IBT/${new Date().getFullYear()}/${String(transfers.length + 1).padStart(3, "0")}`,
      narration: newTransfer.narration || `${newTransfer.type} transfer from ${fromName} to ${toName}`,
      createdBy: user?.name || "User",
      createdAt: new Date().toISOString(),
    };

    setTransfers((prev) => [transfer, ...prev]);
    setNewTransfer({ fromBranchId: "", toBranchId: "", type: "Stock", narration: "", items: [{ name: "", quantity: 1, rate: 0, value: 0, hsnCode: "" }] });
    setDialogOpen(false);
    toast({ title: "Transfer created", description: `${transfer.reference} - ₹${totalValue.toLocaleString("en-IN")} pending approval.` });
  };

  const approveTransfer = (id: string) => {
    setTransfers((prev) => prev.map((t) => t.id === id ? { ...t, status: "Completed" as TransferStatus, approvedBy: user?.name, approvedAt: new Date().toISOString() } : t));
    toast({ title: "Transfer approved & completed" });
  };

  const rejectTransfer = (id: string) => {
    setTransfers((prev) => prev.map((t) => t.id === id ? { ...t, status: "Rejected" as TransferStatus } : t));
    toast({ title: "Transfer rejected", variant: "destructive" });
  };

  const fmt = (n: number) => `₹${n.toLocaleString("en-IN")}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inter-Branch Accounting</h1>
          <p className="text-muted-foreground">Record and track transactions between branches with automatic double-entry accounting</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="mr-2 h-4 w-4" /> New Transfer</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Inter-Branch Transfer</DialogTitle>
              <DialogDescription>Record a transaction between branches. Journal entries will be auto-generated.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Branch</Label>
                  <Select value={newTransfer.fromBranchId} onValueChange={(v) => setNewTransfer((p) => ({ ...p, fromBranchId: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>{branches.filter((b) => b.isActive).map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To Branch</Label>
                  <Select value={newTransfer.toBranchId} onValueChange={(v) => setNewTransfer((p) => ({ ...p, toBranchId: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select destination" /></SelectTrigger>
                    <SelectContent>{branches.filter((b) => b.isActive && b.id !== newTransfer.fromBranchId).map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Transfer Type</Label>
                <Select value={newTransfer.type} onValueChange={(v) => setNewTransfer((p) => ({ ...p, type: v as TransferType }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Stock">Stock / Inventory</SelectItem>
                    <SelectItem value="Funds">Funds Transfer</SelectItem>
                    <SelectItem value="Assets">Fixed Assets</SelectItem>
                    <SelectItem value="Expense Recharge">Expense Recharge</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Items</Label>
                  <Button variant="outline" size="sm" onClick={addItem}><Plus className="mr-1 h-3 w-3" /> Add Item</Button>
                </div>
                {newTransfer.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end border rounded-lg p-3 bg-muted/30">
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Item Name</Label>
                      <Input value={item.name} onChange={(e) => updateItem(idx, "name", e.target.value)} placeholder="Item name" />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">HSN</Label>
                      <Input value={item.hsnCode} onChange={(e) => updateItem(idx, "hsnCode", e.target.value)} placeholder="HSN" />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" min={1} value={item.quantity} onChange={(e) => updateItem(idx, "quantity", +e.target.value)} />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Rate (₹)</Label>
                      <Input type="number" min={0} value={item.rate} onChange={(e) => updateItem(idx, "rate", +e.target.value)} />
                    </div>
                    <div className="col-span-2 flex items-end gap-1">
                      <span className="text-sm font-medium pb-2">{fmt(item.quantity * item.rate)}</span>
                      {newTransfer.items.length > 1 && (
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeItem(idx)}>×</Button>
                      )}
                    </div>
                  </div>
                ))}
                <div className="text-right font-semibold text-lg">
                  Total: {fmt(newTransfer.items.reduce((s, i) => s + i.quantity * i.rate, 0))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Narration</Label>
                <Textarea value={newTransfer.narration} onChange={(e) => setNewTransfer((p) => ({ ...p, narration: e.target.value }))} placeholder="Transfer description..." />
              </div>

              {/* Auto journal preview */}
              {newTransfer.fromBranchId && newTransfer.toBranchId && newTransfer.items[0]?.rate > 0 && (
                <Card className="border-dashed">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Auto Journal Entries (Preview)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Ledger</TableHead><TableHead>Branch</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>{newTransfer.type === "Stock" ? "Branch Stock (Inventory)" : newTransfer.type === "Funds" ? "Branch Current A/c" : "Branch A/c"}</TableCell>
                          <TableCell className="font-medium">{getBranchName(newTransfer.toBranchId)}</TableCell>
                          <TableCell className="text-right text-green-600 font-medium">{fmt(newTransfer.items.reduce((s, i) => s + i.quantity * i.rate, 0))}</TableCell>
                          <TableCell className="text-right">-</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{newTransfer.type === "Stock" ? "Branch Stock (Inventory)" : newTransfer.type === "Funds" ? "Branch Current A/c" : "Branch A/c"}</TableCell>
                          <TableCell className="font-medium">{getBranchName(newTransfer.fromBranchId)}</TableCell>
                          <TableCell className="text-right">-</TableCell>
                          <TableCell className="text-right text-red-600 font-medium">{fmt(newTransfer.items.reduce((s, i) => s + i.quantity * i.rate, 0))}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTransfer}>Create Transfer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><IndianRupee className="h-5 w-5 text-primary" /></div>
            <div><p className="text-sm text-muted-foreground">Total Transferred</p><p className="text-2xl font-bold">{fmt(totalTransferred)}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-chart-2/10"><ArrowLeftRight className="h-5 w-5 text-chart-2" /></div>
            <div><p className="text-sm text-muted-foreground">Total Transfers</p><p className="text-2xl font-bold">{transfers.length}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-chart-3/10"><Calendar className="h-5 w-5 text-chart-3" /></div>
            <div><p className="text-sm text-muted-foreground">Pending / In-Transit</p><p className="text-2xl font-bold">{pendingCount}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-chart-4/10"><Building2 className="h-5 w-5 text-chart-4" /></div>
            <div><p className="text-sm text-muted-foreground">Branches Active</p><p className="text-2xl font-bold">{branches.filter((b) => b.isActive).length}</p></div>
          </div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="transfers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transfers"><ArrowLeftRight className="mr-1 h-4 w-4" /> Transfers</TabsTrigger>
          <TabsTrigger value="journal"><FileText className="mr-1 h-4 w-4" /> Journal Entries</TabsTrigger>
          <TabsTrigger value="reports"><BarChart3 className="mr-1 h-4 w-4" /> Reports</TabsTrigger>
        </TabsList>

        {/* TRANSFERS TAB */}
        <TabsContent value="transfers" className="space-y-4">
          <div className="flex gap-3">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Stock">Stock</SelectItem>
                <SelectItem value="Funds">Funds</SelectItem>
                <SelectItem value="Assets">Assets</SelectItem>
                <SelectItem value="Expense Recharge">Expense Recharge</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>From → To</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filteredTransfers.map((t) => (
                    <TableRow key={t.id} className="cursor-pointer" onClick={() => setSelectedTransfer(t)}>
                      <TableCell className="font-mono text-xs">{t.reference}</TableCell>
                      <TableCell>{t.date}</TableCell>
                      <TableCell className="text-sm">
                        <span className="font-medium">{getBranchName(t.fromBranchId).split(" ")[0]}</span>
                        <span className="mx-1 text-muted-foreground">→</span>
                        <span className="font-medium">{getBranchName(t.toBranchId).split(" ")[0]}</span>
                      </TableCell>
                      <TableCell><Badge variant="outline">{t.type}</Badge></TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm">{t.description}</TableCell>
                      <TableCell className="text-right font-medium">{fmt(t.totalValue)}</TableCell>
                      <TableCell><Badge variant={STATUS_VARIANT[t.status]}>{t.status}</Badge></TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(t.status === "Pending" || t.status === "Approved") && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => approveTransfer(t.id)}>Approve</Button>
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => rejectTransfer(t.id)}>Reject</Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTransfers.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No transfers found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* JOURNAL ENTRIES TAB */}
        <TabsContent value="journal" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Auto-Generated Journal Entries</CardTitle>
              <CardDescription>Double-entry accounting records created automatically for each branch transfer</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Transfer Ref</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Ledger Account</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-right">Debit (₹)</TableHead>
                  <TableHead className="text-right">Credit (₹)</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {transfers.filter((t) => t.status === "Completed").flatMap((t) =>
                    t.journalEntries.map((je, idx) => (
                      <TableRow key={`${t.id}-${idx}`}>
                        {idx === 0 && <TableCell rowSpan={t.journalEntries.length} className="font-mono text-xs">{t.reference}</TableCell>}
                        {idx === 0 && <TableCell rowSpan={t.journalEntries.length}>{t.date}</TableCell>}
                        <TableCell>{je.ledger}</TableCell>
                        <TableCell className="font-medium">{je.branch}</TableCell>
                        <TableCell className="text-right">{je.debit > 0 ? fmt(je.debit) : "-"}</TableCell>
                        <TableCell className="text-right">{je.credit > 0 ? fmt(je.credit) : "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REPORTS TAB */}
        <TabsContent value="reports" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Branch Transfer Volume</CardTitle>
                <CardDescription>Sent vs received value by branch</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={branchTransferVolume}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" className="text-xs" />
                    <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} className="text-xs" />
                    <Tooltip formatter={(v: number) => fmt(v)} />
                    <Legend />
                    <Bar dataKey="sent" fill="hsl(var(--primary))" name="Sent" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="received" fill="hsl(var(--chart-2))" name="Received" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Transfer Type Distribution</CardTitle>
                <CardDescription>Breakdown by transfer category</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={typeDistribution} cx="50%" cy="50%" outerRadius={100} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {typeDistribution.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Monthly Transfer Trend</CardTitle>
                <CardDescription>Total value of completed transfers over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="month" className="text-xs" />
                    <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} className="text-xs" />
                    <Tooltip formatter={(v: number) => fmt(v)} />
                    <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))" }} name="Transfer Value" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Transfer detail dialog */}
      <Dialog open={!!selectedTransfer} onOpenChange={(o) => !o && setSelectedTransfer(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Transfer Details - {selectedTransfer?.reference}</DialogTitle>
            <DialogDescription>
              {selectedTransfer?.date} · <Badge variant={STATUS_VARIANT[selectedTransfer?.status || "Pending"]}>{selectedTransfer?.status}</Badge>
            </DialogDescription>
          </DialogHeader>
          {selectedTransfer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">From:</span> <span className="font-medium">{getBranchName(selectedTransfer.fromBranchId)}</span></div>
                <div><span className="text-muted-foreground">To:</span> <span className="font-medium">{getBranchName(selectedTransfer.toBranchId)}</span></div>
                <div><span className="text-muted-foreground">Type:</span> <Badge variant="outline">{selectedTransfer.type}</Badge></div>
                <div><span className="text-muted-foreground">Value:</span> <span className="font-bold">{fmt(selectedTransfer.totalValue)}</span></div>
              </div>
              <div className="text-sm"><span className="text-muted-foreground">Narration:</span> {selectedTransfer.narration}</div>

              <Card className="border-dashed">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Items</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Item</TableHead><TableHead>HSN</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Value</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {selectedTransfer.items.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell className="text-muted-foreground">{item.hsnCode || "-"}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right">{fmt(item.rate)}</TableCell>
                          <TableCell className="text-right font-medium">{fmt(item.value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="border-dashed">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Journal Entries</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Ledger</TableHead><TableHead>Branch</TableHead><TableHead className="text-right">Dr</TableHead><TableHead className="text-right">Cr</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {selectedTransfer.journalEntries.map((je, i) => (
                        <TableRow key={i}>
                          <TableCell>{je.ledger}</TableCell>
                          <TableCell className="font-medium">{je.branch}</TableCell>
                          <TableCell className="text-right text-green-600">{je.debit > 0 ? fmt(je.debit) : "-"}</TableCell>
                          <TableCell className="text-right text-red-600">{je.credit > 0 ? fmt(je.credit) : "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <div className="text-xs text-muted-foreground">
                Created by {selectedTransfer.createdBy} on {new Date(selectedTransfer.createdAt).toLocaleString()}
                {selectedTransfer.approvedBy && <> · Approved by {selectedTransfer.approvedBy}</>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
